import { LitElement, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";

import "@phosphor-icons/webcomponents/PhFloppyDisk";

import type { SaveProfileParams } from "api-contracts/rpc/save-profile";
import { DataQueryController } from "ui-common/data-query-controller";
import { WowFormController } from "ui-common/form/wow-form-controller";
import { LayoutController } from "ui-common";
import { getUsernamePrefix, platformConfigs } from "ui-common/socialMediaConfig";
import "ui-components/button/wow-button";
import "ui-components/confirmation/wow-confirmation";
import "ui-components/file-upload/file-upload";
import "ui-components/form-elements/textarea/textarea";
import { NotificationService } from "ui-components/notification/notification-service";
import type { RoutePage } from "ui-components/router/wow-router";
import "ui-components/social-media-item/social-media-item";
import { isFan, minimalViableCreator } from "helpers/minimalViableProfile";

import { styles } from "./profile-page.css";

import "../../components/avatar-editor/avatar-editor";
import "../../components/content-container/content-container";
import { scrollableColumnStyles } from "../../components/content-container/content-container.css";
import "../../components/custom-color-picker/custom-color-picker";
import "../../components/page-content/page-content";
import "../../components/profile-color-selector/profile-color-selector";
import type { SocialLinksSection } from "../../components/social-links-section/social-links-section";
import "../../components/social-links-section/social-links-section";
import "../../components/user-background-image/user-background-image";
import { profileDataQueryOptions } from "../../queries/profile-data";
import { unifiedLinksQueryOptions } from "../../queries/unified-links";

import { saveProfileMutation } from "../../services/profile-mutations";
import { t, translate } from "../../translations/i18n";
import type { PreviewProfile } from "../../components/preview-profile";
import "../../components/view-primary-buttons";
import { appContext } from "ui-common/appContext";

// Form data interface for form fields only (fields that have DOM elements)
interface ProfileFormData {
  bio: string;
}

@customElement("profile-page")
export class ProfilePage extends LitElement implements RoutePage {
  static override styles = [styles, scrollableColumnStyles];

  @state()
  private isAddSocialModalOpen = false;

  @state()
  private pendingCoverFileId?: string;

  @state()
  private pendingAvatarFileId?: string;

  @state()
  private isFormInitialized = false; // Track if form has been initialized to prevent unwanted resets

  @state()
  private newlyAddedLinkIndex: number = -1; // Track index of newly added link for auto-focus

  @state()
  private isCustomColorModalOpen = false;

  // Managed state for non-form fields but tracked for dirty detection
  @state()
  private selectedThemeId: string | undefined = undefined;

  @state()
  private pendingCustomColors?: { primary: string; secondary: string };

  @state()
  private currentSocialLinks: string[] = [];

  // Store initial values for dirty checking
  private initialSelectedThemeId: string | undefined = undefined;
  private initialCustomColors?: { primary: string; secondary: string };
  private initialSocialLinks: string[] = [];

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);
  private layoutController = new LayoutController(this);
  private previewProfileRef = createRef<PreviewProfile>();
  private unifiedLinksQuery = new DataQueryController(this, () => unifiedLinksQueryOptions);

  private formController!: WowFormController<ProfileFormData>;

  constructor() {
    super();
    this.formController = new WowFormController<ProfileFormData>(
      this,
      {
        bio: "",
      },
      {
        validateOnBlur: true,
        validators: {
          bio: [
            (value) => {
              if (value && value.length > 500) {
                return translate("profile.bioValidation");
              }
              return undefined;
            },
          ],
        },
      },
      (event) => this.handleProfileSave(event),
    );

    // Listen for social links updated event
    this.addEventListener("social-links-updated", this.handleSocialLinksUpdated as EventListener);
  }

  private handleColorSelect(id: string) {
    this.selectedThemeId = id;
  }

  private getCurrentCustomColors(): { primary: string; secondary: string } {
    const profileData = this.profileDataQuery.result.data;
    // Return current custom colors if available, otherwise default colors
    return {
      primary: profileData?.customColors?.primary || "#FF5733",
      secondary: profileData?.customColors?.secondary || "#33C1FF",
    };
  }

  private getCurrentThemeColors(): { primary: string; secondary: string } | undefined {
    const profileData = this.profileDataQuery.result.data;

    // Priority 1: If a theme is selected (via selectedThemeId), find and use that theme's colors
    if (this.selectedThemeId && profileData?.colors) {
      const selectedTheme = profileData.colors.find((color) => color.id === this.selectedThemeId);
      if (selectedTheme) {
        return {
          primary: selectedTheme.primary,
          secondary: selectedTheme.secondary,
        };
      }
    }

    // Priority 2: If no theme is selected but custom colors exist, use pending custom colors
    if (this.selectedThemeId === undefined && this.pendingCustomColors) {
      return {
        primary: this.pendingCustomColors.primary,
        secondary: this.pendingCustomColors.secondary,
      };
    }

    // No dynamic state available
    return undefined;
  }

  private handleCustomColorSelect() {
    this.isCustomColorModalOpen = true;
    // Set special index to indicate custom mode
    this.selectedThemeId = undefined;
  }

  private handleCustomColorSave(event: CustomEvent) {
    const { colors } = event.detail;
    this.pendingCustomColors = colors;
    this.isCustomColorModalOpen = false;
  }

  private handleCustomColorCancel() {
    this.isCustomColorModalOpen = false;
  }

  protected override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    // Initialize form with profile data when it becomes available - but only ONCE
    const profileData = this.profileDataQuery.result.data;
    if (profileData && !this.isFormInitialized) {
      // Initialize form controller with only form fields
      this.formController.reset({
        bio: profileData.bio || "",
      });

      // Initialize state for non-form fields
      this.selectedThemeId = profileData.theme?.id;
      this.pendingCustomColors = this.getCurrentCustomColors();
      this.currentSocialLinks = profileData.socialLinks || [];

      // Store initial values for dirty checking
      this.initialSelectedThemeId = profileData.theme?.id;
      this.initialCustomColors = this.getCurrentCustomColors();
      this.initialSocialLinks = profileData.socialLinks || [];

      this.isFormInitialized = true; // Mark as initialized
    }

    if (profileData && isFan(profileData)) {
      const url = new URL("/explore", window.location.origin);
      window.location.href = url.toString();
      return;
    }

    if (profileData && !minimalViableCreator(profileData)) {
      const url = new URL("/my/onboard-creator", window.location.origin);
      url.searchParams.set("step", "1");
      window.location.href = url.toString();
    }
  }

  // Handle cover image upload event
  private handleCoverImageUploaded = (event: CustomEvent) => {
    const { fileId } = event.detail;
    this.pendingCoverFileId = fileId;
  };

  // Handle avatar upload event
  private handleAvatarUploaded = (event: CustomEvent) => {
    const { fileId } = event.detail;
    this.pendingAvatarFileId = fileId;
  };

  private handleAddMoreClicked() {
    this.isAddSocialModalOpen = true;
  }

  private handleModalClose() {
    this.isAddSocialModalOpen = false;
    this.editSocialDetail = undefined;
  }

  private handleSocialLinksUpdated(event: CustomEvent) {
    const { socialLinks } = event.detail;
    this.currentSocialLinks = socialLinks; // This now uses form controller setter
  }

  private handleSocialLinksChanged(event: CustomEvent) {
    const { socialLinks } = event.detail;
    this.currentSocialLinks = socialLinks; // This now uses form controller setter
  }

  private handleAutoFocusUsed() {
    // Clear the auto-focus index after it's been used
    this.newlyAddedLinkIndex = -1;
  }

  private editSocialDetail: { platformName: string; index: number } | undefined = undefined;

  private handleEditPlatformClicked(event: CustomEvent) {
    const { platform, index } = event.detail;

    this.editSocialDetail = { platformName: platform, index };
    this.isAddSocialModalOpen = true;
  }

  private handleAddLinkClicked(event: CustomEvent) {
    const { platform } = event.detail;

    const platformConfig = platformConfigs.find((p) => p.platform === platform);
    if (!platformConfig) {
      return;
    }

    let newLinkUrl = "";

    if (this.editSocialDetail) {
      const { index, platformName } = this.editSocialDetail;

      const oldPlatformConfig = platformConfigs.find((p) => p.platformName === platformName);
      const link = this.currentSocialLinks[index] ?? "";

      if (!oldPlatformConfig || !link) return;

      // Extract username from existing URL using regex
      const oldDomain = oldPlatformConfig.domains[0] ?? "";
      const oldPrefix = getUsernamePrefix(oldPlatformConfig.platform);

      // Match pattern like "https://domain/...prefix...username"
      const usernameRegex = new RegExp(`${oldDomain}/(?:${oldPrefix})?([^/?#]+)`, "i");
      const match = link.match(usernameRegex);
      const username = match?.[1] ?? "";

      // Build new platform link
      const newDomain = platformConfig.domains[0] ?? "";
      const newPrefix = getUsernamePrefix(platformConfig.platform);

      // Rebuild full URL using extracted username
      if (platformConfig.platform === "email") {
        newLinkUrl = username; // For email, username is full address
      } else {
        newLinkUrl = `https://${newDomain}/${newPrefix}${username}`;
      }

      // Replace link in array
      this.currentSocialLinks = this.currentSocialLinks.map((l, i) => (i === index ? newLinkUrl : l));

      // reset edit link detail
      this.editSocialDetail = undefined;

      this.newlyAddedLinkIndex = index;
    } else {
      if (platformConfig.platform === "email") {
        this.currentSocialLinks = [...this.currentSocialLinks, newLinkUrl];
      } else {
        // Create new link URL and add to current social links
        const usernamePrefix = getUsernamePrefix(platformConfig.platform);
        newLinkUrl = `https://${platformConfig.domains[0]}/${usernamePrefix}`;
        this.currentSocialLinks = [...this.currentSocialLinks, newLinkUrl];
      }
      // Track the newly added link index for auto-focus
      this.newlyAddedLinkIndex = this.currentSocialLinks.length - 1;
    }

    // Emit event to update form state
    this.dispatchEvent(
      new CustomEvent("social-links-updated", {
        detail: { socialLinks: this.currentSocialLinks },
        bubbles: true,
      }),
    );

    // Close modal immediately
    this.isAddSocialModalOpen = false;
  }

  private renderSocialLinksInModal() {
    // Split platforms into two columns for consistent top-to-bottom, left-to-right ordering
    const leftColumnPlatforms = platformConfigs.slice(0, Math.ceil(platformConfigs.length / 2));
    const rightColumnPlatforms = platformConfigs.slice(Math.ceil(platformConfigs.length / 2));

    return html`
      <div class="social-links-content">
        <div class="column-left">${this.renderPlatformAddButtons(leftColumnPlatforms)}</div>
        <div class="column-right">${this.renderPlatformAddButtons(rightColumnPlatforms)}</div>
      </div>
    `;
  }

  private renderPlatformAddButtons(platforms: (typeof platformConfigs)[number][]) {
    return platforms.map((platform) => {
      return html`
        <social-media-item
          .icon=${platform.colorfulIcon}
          .platformName=${platform.platformName}
          .platform=${platform.platform}
          .mode=${"add-button"}
          .addText=${t("profile.addLink")}
          .addAriaLabel=${t("profile.addLinkAriaLabel", { platformName: platform.platformName })}
          @add-link-clicked=${this.handleAddLinkClicked}
        ></social-media-item>
      `;
    });
  }

  private handleSocialLinkClicked() {
    // Handle social link click action
    // TODO: Implement social link click handling
  }

  private async handleProfileSave(event: { data?: ProfileFormData; isValid: boolean }) {
    if (!event.isValid || !event.data) return;

    // Validate social links
    const socialLinksSection = this.shadowRoot?.querySelector("social-links-section") as SocialLinksSection | null;
    if (socialLinksSection && !socialLinksSection.validateAllLinks()) {
      // Social links validation failed, prevent save
      NotificationService.error(translate("errors.invalidSocialLinks"));
      return;
    }

    const currentProfile = this.profileDataQuery.result.data;
    if (!currentProfile) return;

    // Build save data with only changed fields - use component state for non-form fields
    const saveData: SaveProfileParams = {};

    // Form fields from event.data
    if (event.data.bio !== currentProfile.bio) {
      saveData.bio = event.data.bio;
    }

    // Theme and custom colors from component state
    if (this.selectedThemeId !== currentProfile.theme?.id) {
      saveData.selectedThemeId = this.selectedThemeId;
    }

    if (
      this.selectedThemeId === undefined &&
      this.pendingCustomColors &&
      (currentProfile.theme !== undefined ||
        this.pendingCustomColors?.primary !== currentProfile.customColors?.primary ||
        this.pendingCustomColors?.secondary !== currentProfile.customColors?.secondary)
    ) {
      saveData.customColors = {
        primary: this.pendingCustomColors.primary,
        secondary: this.pendingCustomColors.secondary,
      };
    }

    // Social links from component state
    const currentSocialLinks = currentProfile.socialLinks || [];
    if (JSON.stringify(currentSocialLinks) !== JSON.stringify(this.currentSocialLinks)) {
      saveData.socialLinks = this.currentSocialLinks;
    }

    // Include pending cover file if uploaded
    if (this.pendingCoverFileId) {
      saveData.profileCoverFileId = this.pendingCoverFileId;
    }

    // Include pending avatar file if uploaded
    if (this.pendingAvatarFileId) {
      saveData.profileImageFileId = this.pendingAvatarFileId;
    }

    // Only save if there are changes
    if (Object.keys(saveData).length > 0) {
      try {
        await saveProfileMutation.mutate(saveData);

        // Clear pending state after successful save
        this.pendingCoverFileId = undefined;
        this.pendingAvatarFileId = undefined;

        // Update initial values after successful save
        this.initialSelectedThemeId = this.selectedThemeId;
        this.initialCustomColors = this.pendingCustomColors;
        this.initialSocialLinks = [...this.currentSocialLinks];

        this.previewProfileRef.value?.reloadIframe();

        // Show success notification
        NotificationService.success(translate("profile.notifications.saveSuccess"));
      } catch (error) {
        // Show error notification
        NotificationService.error(translate("profile.notifications.saveError"));
        throw error; // Re-throw to handle in form controller
      }
    } else {
      // No changes to save
      NotificationService.info(translate("profile.notifications.noChanges"));
    }
  }

  /**
   * Check if form fields or non-form fields have unsaved changes
   */
  private hasUnsavedChanges(): boolean {
    // Check form controller for form fields
    if (this.formController.isDirty()) {
      return true;
    }

    // Check file uploads
    if (this.pendingCoverFileId || this.pendingAvatarFileId) {
      return true;
    }

    // Check non-form fields
    if (this.selectedThemeId !== this.initialSelectedThemeId) {
      return true;
    }

    if (JSON.stringify(this.pendingCustomColors) !== JSON.stringify(this.initialCustomColors)) {
      return true;
    }

    if (JSON.stringify(this.currentSocialLinks) !== JSON.stringify(this.initialSocialLinks)) {
      return true;
    }

    return false;
  }

  /**
   * Check if navigation should be allowed - called by router when  tries to navigate away
   */
  async canDeactivate(): Promise<boolean> {
    // Check if there are any unsaved changes
    if (!this.hasUnsavedChanges()) {
      return true; // No changes, allow navigation
    }

    // Form has changes, show confirmation modal
    return new Promise((resolve) => {
      this.showUnsavedChangesModal(resolve);
    });
  }

  @state()
  private isUnsavedChangesModalOpen = false;

  @state()
  private unsavedChangesResolver?: (result: boolean) => void;

  private showUnsavedChangesModal(resolver: (result: boolean) => void) {
    this.unsavedChangesResolver = resolver;
    this.isUnsavedChangesModalOpen = true;
  }

  private handleUnsavedChangesContinueEditing() {
    this.isUnsavedChangesModalOpen = false;
    if (this.unsavedChangesResolver) {
      this.unsavedChangesResolver(false);
    }
  }

  private async handleUnsavedChangesSaveAndContinue() {
    try {
      // Attempt to save the form
      const success = await this.formController.submit();

      this.isUnsavedChangesModalOpen = false;
      if (this.unsavedChangesResolver) {
        this.unsavedChangesResolver(success); // Allow navigation if save was successful
        this.unsavedChangesResolver = undefined;
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      // Keep modal open if save failed
    }
  }

  private async handleSaveAndContinue() {
    try {
      await this.formController.submit();
    } catch (error) {
      console.log("Error saving profile:", error);
    }
  }

  private handleUnsavedChangesDiscard() {
    this.isUnsavedChangesModalOpen = false;
    if (this.unsavedChangesResolver) {
      this.unsavedChangesResolver(true); // Allow navigation without saving
      this.unsavedChangesResolver = undefined;
    }
  }

  private handleUnsavedChangesClose() {
    this.isUnsavedChangesModalOpen = false;
    // Restore body scroll when drawer closes
    document.body.style.overflow = "unset";
    if (this.unsavedChangesResolver) {
      this.unsavedChangesResolver(false); // Cancel navigation
      this.unsavedChangesResolver = undefined;
    }
  }

  private handleUnsavedChangesModalClose() {
    this.isUnsavedChangesModalOpen = false;
    if (this.unsavedChangesResolver) {
      this.unsavedChangesResolver(false); // Cancel navigation
      this.unsavedChangesResolver = undefined;
    }
  }

  private handleOpenProfile = () => {
    const handle = appContext.currentUser?.handle;

    if (handle) window.open(`/${handle}/`, "_self", "noopener,noreferrer");
  };

  // private renderLinkCta() {
  //   const { data, isLoading } = this.unifiedLinksQuery.result;
  //   const totalLinks = data?.counts.total || 0;

  //   if (isLoading || totalLinks > 0) return nothing;

  //   const handleCtaClick = () => {
  //     navigateTo("links");
  //   };

  //   return html`
  //     <cta-banner
  //       heading="${t("profile.ctaLink.title")}"
  //       description="${t("profile.ctaLink.description")}"
  //       buttonLabel="${t("profile.ctaLink.buttonLabel")}"
  //       @cta-click=${handleCtaClick}
  //     ></cta-banner>
  //   `;
  // }

  private renderAddSocialPlatformModal() {
    if (this.isAddSocialModalOpen) {
      const modalTitle = this.editSocialDetail ? t("profile.editSocialPlatform") : t("profile.addSocialPlatform");

      return html`
        <wow-modal @close=${this.handleModalClose} variant="responsive">
          <span slot="header">${modalTitle}</span>
          ${this.renderSocialLinksInModal()}
        </wow-modal>
      `;
    }

    return nothing;
  }

  protected override render() {
    const { data: profileData } = this.profileDataQuery.result;

    // Convert relative paths to full URLs

    // Get current theme colors for real-time preview
    const currentColors = this.getCurrentThemeColors();

    return html`
      <page-content pageTitle=${t("pages.editProfile")}>
        <div slot="actions">
          <view-primary-buttons
            primaryText=${t("profile.save")}
            formId="profile-form"
            @primary-click=${this.handleSaveAndContinue}
            @view-click=${this.handleOpenProfile}
          >
            <ph-floppy-disk slot="primaryIcon" size="18"></ph-floppy-disk>
          </view-primary-buttons>
        </div>

        <section class="containers">
          <content-container variant="primary" columns="2" class="profile-content-container">
            <form id="profile-form" slot="column" class="user-info-section">
              <user-background-image
                .primaryColor=${currentColors?.primary}
                .secondaryColor=${currentColors?.secondary}
                .socialLinks=${this.currentSocialLinks}
                @cover-image-uploaded=${this.handleCoverImageUploaded}
              >
                <wow-avatar-editor @avatar-uploaded=${this.handleAvatarUploaded}></wow-avatar-editor>
              </user-background-image>

              <profile-color-selector
                .selectedId=${this.selectedThemeId}
                .primaryColor=${this.pendingCustomColors?.primary}
                .secondaryColor=${this.pendingCustomColors?.secondary}
                @color-selected=${(e: CustomEvent) => this.handleColorSelect(e.detail.id)}
                @custom-color-selected=${this.handleCustomColorSelect}
              ></profile-color-selector>

              <wow-input
                form="none"
                disabled
                placeholder=${t("profile.handle")}
                type="text"
                .value=${profileData?.handle ?? ""}
              ></wow-input>
              <wow-textarea
                name=${this.formController.fieldNames.bio}
                placeholder=${t("profile.bio")}
                maxlength="500"
                .value=${profileData?.bio ?? ""}
              ></wow-textarea>
            </form>

            <div slot="column">
              <social-links-section
                .socialLinks=${this.currentSocialLinks}
                .editable=${true}
                .autoFocusIndex=${this.newlyAddedLinkIndex}
                @add-more-clicked=${this.handleAddMoreClicked}
                @social-link-clicked=${this.handleSocialLinkClicked}
                @social-links-changed=${this.handleSocialLinksChanged}
                @auto-focus-used=${this.handleAutoFocusUsed}
                @edit-platform-clicked=${this.handleEditPlatformClicked}
              ></social-links-section>
            </div>
          </content-container>

          ${this.layoutController.isUltrawide
            ? html`
                <content-container variant="secondary" columns="1">
                  <div slot="column">
                    <preview-profile page="/" ${ref(this.previewProfileRef)}></preview-profile>
                  </div>
                </content-container>
              `
            : nothing}
          ${this.renderAddSocialPlatformModal()}
          ${this.isCustomColorModalOpen
            ? html`
                <custom-color-picker
                  .initialColors=${this.pendingCustomColors}
                  @save=${this.handleCustomColorSave}
                  @cancel=${this.handleCustomColorCancel}
                ></custom-color-picker>
              `
            : ""}
          ${this.isUnsavedChangesModalOpen
            ? html`
                <wow-confirmation header=${t("profile.unsavedChanges")} @close=${this.handleUnsavedChangesModalClose}>
                  <div class="unsaved-changes-content">
                    <p>${t("profile.unsavedChangesMessage")}</p>
                    <wow-button variant="primary" size="medium" @click=${this.handleUnsavedChangesSaveAndContinue}>
                      ${t("profile.saveAndContinue")}
                    </wow-button>
                    <wow-button variant="neutral" size="medium" @click=${this.handleUnsavedChangesContinueEditing}>
                      ${t("profile.continueEditing")}
                    </wow-button>
                    <wow-button
                      class="danger"
                      variant="neutral"
                      size="medium"
                      @click=${this.handleUnsavedChangesDiscard}
                    >
                      ${t("profile.discardChanges")}
                    </wow-button>
                  </div>
                </wow-confirmation>
              `
            : ""}
        </section>
      </page-content>
    `;
  }
}
